import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import DatePicker from "../../../../Common/DatePicker";
import { DsButton, DsIcon, DsInput, DsLabel } from "../../../../../design-system";
import InfoHint from "./InfoHint";
import {
  EMPTY_HOLIDAY_BANK_FORM,
  HOLIDAY_TYPE_OPTIONS,
} from "../../../../../lib/holidayBankUtils";

/**
 * Create / Edit a single holiday bank entry.
 */
export default function HolidayBankFormModal({
  open,
  darkMode,
  editingHoliday = null,
  nestedUnderPolicy = false,
  onClose,
  onSave,
  saving = false,
}) {
  const [form, setForm] = useState(EMPTY_HOLIDAY_BANK_FORM);

  useEffect(() => {
    if (!open) return;
    if (editingHoliday) {
      setForm({
        date: editingHoliday.date || "",
        name: editingHoliday.name || "",
        type: editingHoliday.type || "national",
        description: editingHoliday.description || "",
      });
    } else {
      setForm({ ...EMPTY_HOLIDAY_BANK_FORM });
    }
  }, [open, editingHoliday]);

  if (!open) return null;

  const isEdit = !!editingHoliday?.id;
  const inputCls = `w-full rounded-xl border px-3 py-2 text-sm border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]`;

  const submit = () => {
    if (!form.date || !String(form.name || "").trim()) return;
    onSave?.(form, editingHoliday);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[11500] flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))] shadow-[var(--ds-shadow-card)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="holiday-bank-form-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] px-5 py-4">
          <div>
            <h2 id="holiday-bank-form-title" className="ds-h3">
              {isEdit ? "Edit holiday" : "Add holiday to bank"}
            </h2>
            <p className="ds-caption mt-1 text-[hsl(var(--ds-muted-fg))]">
              {nestedUnderPolicy
                ? "Saved entries appear in the policy holiday picker."
                : "Company-wide holiday catalog used by holiday policies."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]"
            aria-label="Close"
          >
            <DsIcon icon={X} size="md" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1 flex items-center gap-1.5">
                <DsLabel>Holiday date *</DsLabel>
                <InfoHint title="Calendar date of the holiday. Used for year-wise grouping and attendance marking." />
              </div>
              <DatePicker
                label="Holiday Date"
                required
                portal
                value={form.date}
                onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div>
              <div className="mb-1 flex items-center gap-1.5">
                <DsLabel>Holiday name *</DsLabel>
                <InfoHint title="Display name shown on calendars, policies, and employee RH selection." />
              </div>
              <DsInput
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Independence Day"
                darkMode={darkMode}
                className="rounded-xl"
              />
            </div>
          </div>

          <div>
            <div className="mb-1 flex items-center gap-1.5">
              <DsLabel>Holiday type *</DsLabel>
              <InfoHint title="Restricted Holiday (RH) dates require employee selection and count toward Max RH / year on the policy. Other types are usually full paid holidays." />
            </div>
            <select
              value={form.type}
              onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}
              className={inputCls}
            >
              {HOLIDAY_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <DsLabel className="mb-1">Description</DsLabel>
            <textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={3}
              placeholder="Optional notes"
              className={inputCls}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
          <DsButton type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </DsButton>
          <DsButton
            type="button"
            variant="primary"
            size="sm"
            disabled={saving || !form.date || !String(form.name || "").trim()}
            onClick={submit}
          >
            {saving ? "Saving…" : isEdit ? "Update holiday" : "Add to bank"}
          </DsButton>
        </div>
      </div>
    </div>,
    document.body
  );
}
